/**
 * Per-variant copy for the join funnel.
 *
 * Source of truth for the strings is the "Positioning Experiment Funnels"
 * Notion page. Step 2 and the Step 3 header are identical across variants;
 * Step 1 (header + value-prop) and the Step 3 value-prop differ.
 */

import type { PositioningVariant } from '@/constants/funnel';

/** Pill icon — matches a curated set of lucide icons resolved in `ValuePropCard`. */
export type ChipIcon = 'book-open' | 'dna' | 'shield-check' | 'microscope';

/** Eyebrow badge icon — circular grey badge to the left of the eyebrow text. */
export type EyebrowIcon = 'stethoscope';

export interface ChipCopy {
  label: string;
  icon?: ChipIcon;
}

export interface ValuePropCopy {
  /** Optional eyebrow displayed in uppercase above the title. */
  eyebrow?: string;
  /** Optional small icon shown in a circular badge to the left of the eyebrow text. */
  eyebrowIcon?: EyebrowIcon;
  /** Bold title sentence. */
  title: string;
  /** Body paragraph(s). */
  body: string;
  /** Pill-shaped chips with stat highlights. */
  chips: ChipCopy[];
}

export interface StepCopy {
  header: string;
  subheader: string;
  valueProp: ValuePropCopy;
}

export interface FunnelStep2Quote {
  text: string;
  attribution: string;
}

export interface FunnelStep2Copy extends StepCopy {
  quote: FunnelStep2Quote;
}

export interface FunnelCopy {
  step1: StepCopy;
  step2: FunnelStep2Copy;
  step3: StepCopy;
}

const STEP2_QUOTE: FunnelStep2Quote = {
  text:
    "The earliest signal of cancer is rarely the tumor itself, it's the years of risk before it. Catch finally makes that window actionable.",
  attribution: 'Dr. Kristi Funke, MD · Medical Advisor, Board-Certified Oncologist',
};

const STEP2_BASE: FunnelStep2Copy = {
  header: 'Start building your profile.',
  subheader: 'Age and biological sex are baseline factors in cancer risk.',
  valueProp: {
    eyebrow: 'SCIENCE YOU CAN TRUST',
    eyebrowIcon: 'stethoscope',
    title: 'Built by leading physicians from Yale, Johns Hopkins, and Columbia University.',
    body: '',
    chips: [
      { label: 'HIPAA aligned', icon: 'shield-check' },
      { label: 'Peer-reviewed', icon: 'book-open' },
    ],
  },
  quote: STEP2_QUOTE,
};

const STEP3_HEADER = "Where are you located?";
const STEP3_SUBHEADER = "We'll recommend a lab near you.";
const STEP3_BODY =
  'Catch looks at your health history, blood biomarkers, and data from your wearable to understand your risk.';
const STEP3_CHIPS: ChipCopy[] = [
  { label: '50+ biomarkers', icon: 'microscope' },
  { label: 'Biological age', icon: 'dna' },
];

const STEP1_BODY =
  'Catch draws from 10,000 peer-reviewed studies to evaluate your risk for 21 cancers.';
const STEP1_CHIPS: ChipCopy[] = [
  { label: '10,000 studies', icon: 'book-open' },
  { label: '500 risk factors', icon: 'dna' },
];

const STEP1_SUBHEADER = 'Learn which cancers are more and less likely to affect you';

export const FUNNEL_COPY: Record<PositioningVariant, FunnelCopy> = {
  'default': {
    step1: {
      header: 'Stay ahead of cancer.',
      subheader: STEP1_SUBHEADER,
      valueProp: {
        title: 'Preventing cancer starts with knowing your risk.',
        body: STEP1_BODY,
        chips: STEP1_CHIPS,
      },
    },
    step2: STEP2_BASE,
    step3: {
      header: STEP3_HEADER,
      subheader: STEP3_SUBHEADER,
      valueProp: {
        eyebrow: 'COMPREHENSIVE RISK PROFILE',
        title: 'Bloodwork is one of three inputs.',
        body: STEP3_BODY,
        chips: STEP3_CHIPS,
      },
    },
  },
  'test-before-test': {
    step1: {
      header: 'Catch is the test before the test.',
      subheader: STEP1_SUBHEADER,
      valueProp: {
        title: 'Every other test waits for cancer. Catch looks ahead.',
        body: STEP1_BODY,
        chips: STEP1_CHIPS,
      },
    },
    step2: STEP2_BASE,
    step3: {
      header: STEP3_HEADER,
      subheader: STEP3_SUBHEADER,
      valueProp: {
        eyebrow: 'COMPREHENSIVE RISK PROFILE',
        title: 'Other tests rely on one input. Catch a comprehensive risk profile.',
        body: STEP3_BODY,
        chips: STEP3_CHIPS,
      },
    },
  },
  'checkup': {
    step1: {
      header: 'Get a preventive cancer checkup.',
      subheader: STEP1_SUBHEADER,
      valueProp: {
        title: 'The healthcare system overlooks cancer risk. Catch fixes that.',
        body: STEP1_BODY,
        chips: STEP1_CHIPS,
      },
    },
    step2: STEP2_BASE,
    step3: {
      header: STEP3_HEADER,
      subheader: STEP3_SUBHEADER,
      valueProp: {
        eyebrow: 'COMPREHENSIVE RISK PROFILE',
        title: 'Checkups are based on generic guidelines. Catch is specific to your biology.',
        body: STEP3_BODY,
        chips: STEP3_CHIPS,
      },
    },
  },
};
